import { isEmpty, isNotEmpty } from '@/shared/util';

import { ESCAPED_HTML_PATTERN } from '../../editor.constant';
import { isLevelHeader } from '../text.util';
import { createBulletPoint } from './builder.util';

function shouldPreserveAsHeader(element: Element): boolean {
  if (element.tagName !== 'P') {
    return false;
  }

  const text = element.textContent?.trim() || '';

  return isLevelHeader(text);
}

/**
 * Checks if an element contains only escaped HTML (e.g., &lt;p&gt;&lt;br&gt;&lt;/p&gt;).
 */
function containsOnlyEscapedHtml(element: Element): boolean {
  if (element.tagName !== 'P') {
    return false;
  }

  const innerHTML = element.innerHTML.trim();
  const textContent = element.textContent?.trim() || '';

  if (isEmpty(innerHTML) || isEmpty(textContent)) {
    return false;
  }

  if (innerHTML === textContent) {
    return false;
  }

  return innerHTML.includes('&lt;') && innerHTML.includes('&gt;');
}

/**
 * Processes a text node, extracting its content for conversion to bullet point.
 * Splits text by sentence boundaries (periods followed by spaces) to create multiple bullet points.
 */
function processTextNode(node: Node, textBeforeOl: string[], nodesToRemove: Node[]): void {
  const parent = node.parentElement;

  if (parent && containsOnlyEscapedHtml(parent)) {
    return;
  }

  const text = node.textContent?.trim();

  if (isNotEmpty(text)) {
    const sentences = text.split(/(?<=\.)\s+/).filter((s) => s.trim());

    sentences.forEach((sentence) => {
      const trimmed = sentence.trim();

      if (isNotEmpty(trimmed)) {
        textBeforeOl.push(trimmed);
      }
    });
  }

  nodesToRemove.push(node);
}

/**
 * Processes an element node, extracting its content if it should be converted.
 */
function processElementNode(
  element: Element,
  textBeforeOl: string[],
  nodesToRemove: Node[],
  targetList: Element,
): boolean {
  if (element.tagName === 'OL' || element.tagName === 'UL') {
    return true;
  }

  if (element.contains(targetList)) {
    return false;
  }

  if (!shouldPreserveAsHeader(element) && !containsOnlyEscapedHtml(element)) {
    const text = element.textContent?.trim();

    if (isNotEmpty(text)) {
      textBeforeOl.push(text);
    }

    nodesToRemove.push(element);
  }

  return false;
}

/**
 * Extracts text content from nodes before the first <ol> element.
 * Preserves paragraph headers and level headers.
 */
function extractTextBeforeList(
  container: Element,
  firstOl: Element,
): {
  nodesToRemove: Node[];
  textBeforeOl: string[];
} {
  const nodesToRemove: Node[] = [];
  const textBeforeOl: string[] = [];
  let node = container.firstChild;

  while (node && node !== firstOl) {
    if (node.nodeType === Node.TEXT_NODE) {
      processTextNode(node, textBeforeOl, nodesToRemove);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;

      if (element.contains(firstOl)) {
        const childResult = extractTextBeforeList(element, firstOl);

        textBeforeOl.push(...childResult.textBeforeOl);
        nodesToRemove.push(...childResult.nodesToRemove);
        break;
      } else {
        const shouldBreak = processElementNode(element, textBeforeOl, nodesToRemove, firstOl);

        if (shouldBreak) {
          break;
        }
      }
    }

    node = node.nextSibling;
  }

  return { nodesToRemove, textBeforeOl };
}

/**
 * Converts text before <ol> tags into bullet points within the list.
 * This handles cases where text appears before a list and should be part of it.
 *
 * @param html - HTML string that may contain text before <ol> tags
 * @returns HTML with text before <ol> converted to bullet points
 *
 * @example
 * ```typescript
 * const input = '\nThis is text.  <ol><li>item 1</li><li>item 2</li></ol>';
 * const output = convertTextBeforeListToBulletPoints(input);
 * // Returns: '<ol><li data-list="bullet"><span class="ql-ui" contenteditable="false"></span>This is text.</li><li data-list="bullet"><span class="ql-ui" contenteditable="false"></span>item 1</li><li data-list="bullet"><span class="ql-ui" contenteditable="false"></span>item 2</li></ol>'
 * ```
 */
export function convertTextBeforeListToBulletPoints(html: string): string {
  if (!html?.includes('<ol')) {
    return html;
  }

  const olIndex = html.indexOf('<ol');

  if (olIndex > 0) {
    const beforeOl = html.substring(0, olIndex);

    if (beforeOl.includes('<p>&lt;') || ESCAPED_HTML_PATTERN.exec(beforeOl)) {
      return html;
    }
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const { body } = doc;

  if (body.children.length === 0) {
    return html;
  }

  const firstOl = body.querySelector('ol');

  if (!firstOl) {
    return html;
  }

  let node = body.firstChild;

  while (node && node !== firstOl) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;

      if (containsOnlyEscapedHtml(element)) {
        return html;
      }
    }

    node = node.nextSibling;
  }

  const { nodesToRemove, textBeforeOl } = extractTextBeforeList(body, firstOl);

  if (isEmpty(textBeforeOl)) {
    return html;
  }

  for (let i = textBeforeOl.length - 1; i >= 0; i--) {
    const text = textBeforeOl[i];
    const li = createBulletPoint(text);

    firstOl.insertBefore(li, firstOl.firstChild);
  }

  nodesToRemove.forEach((nodeToRemove) => {
    (nodeToRemove as ChildNode).remove();
  });

  return body.innerHTML.trim();
}
